"use client";

import { type DialogProps, Root as RootPrimitive } from "@radix-ui/react-dialog";
import { type ReactNode, useCallback, useId, useMemo, useRef, useState } from "react";

import { DialogRootContext } from "./context";
import { DialogNestedContext, useDialogStackContext } from "./stack/context";

export type DialogRootProps = DialogProps & {
  /**
   * Force a dialog of its own even when rendered inside a `Dialog.Stack`.
   *
   * Inside a stack a nested `Dialog.Root` resolves to a pane that slides in over the view below.
   * Set this when the nested dialog must genuinely layer on top instead — a destructive
   * confirmation raised from a pushed view, for example.
   */
  standalone?: boolean;
  children?: ReactNode;
};

const Root = ({ children, standalone = false, ...props }: DialogRootProps) => {
  const stack = useDialogStackContext();
  const paneKey = useId();
  const contentRef = useRef<HTMLDivElement>(null as unknown as HTMLDivElement);

  const [uncontrolledOpen, setUncontrolledOpen] = useState(props.defaultOpen ?? false);
  const isControlled = props.open !== undefined;
  const resolvedOpen = isControlled ? Boolean(props.open) : uncontrolledOpen;

  const propsRef = useRef(props);
  propsRef.current = props;

  const setOpen = useCallback((next: boolean) => {
    setUncontrolledOpen(next);
    propsRef.current.onOpenChange?.(next);
  }, []);

  const rootValue = useMemo(() => ({ contentRef }), []);
  const nestedValue = useMemo(() => ({ paneKey, open: resolvedOpen, setOpen }), [paneKey, resolvedOpen, setOpen]);

  if (stack && !standalone) {
    return (
      <DialogRootContext.Provider value={rootValue}>
        <DialogNestedContext.Provider value={nestedValue}>{children}</DialogNestedContext.Provider>
      </DialogRootContext.Provider>
    );
  }

  return (
    <DialogRootContext.Provider value={rootValue}>
      <RootPrimitive {...props}>{children}</RootPrimitive>
    </DialogRootContext.Provider>
  );
};

Root.displayName = RootPrimitive.displayName;

export default Root;
